import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Stack, Shelf } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading } from "capitalrx-components-2.0/lib/typography";
import { Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Form, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Field as TextField } from "capitalrx-components-2.0/lib/forms/Textbox/Field";

import { getProviderManagementAPI } from "@/api";
import { CaseSearchErrors } from "@/constants";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeButton, getThemeURL, isDate, numberValidator, formatDateWithSlashes } from "@/utils";

import styles from "./PASearchForm.module.scss";

export interface CaseSearchForm {
  readonly case_id: number;
  readonly prescriber_npi: string;
  readonly external_member_id: string;
  readonly member_date_of_birth: string;
  readonly prescriber_first: string;
  readonly prescriber_last: string;
}

interface HidenCaseSearchForm {
  readonly case_id: string | number;
  readonly prescriber_npi: string;
  readonly external_member_id: string;
  readonly member_date_of_birth: string;
  readonly prescriber_first: string;
  readonly prescriber_last: string;
}

interface PASearchFormProps {
  setSelectedTab?: (string) => void;
  showTitle?: boolean;
}

export const PASearchForm: FC<PASearchFormProps> = ({ setSelectedTab, showTitle=false }) => {
  const navigate = useNavigate();
  const { getAuthToken } = useContext(AuthenticationContext);
  const { primary_color, primary_hover_color } = useContext(ThemeSelectorContext);

  const form = useForm<HidenCaseSearchForm>();
  const { setValue } = form;

  const [error, setError] = useState<string>();

  const [originalMemberId, setOriginalMemberId] = useState<string>("");
  const [originalDOB, setOriginalDOB] = useState<string>("");
  const [originalCaseId, setOriginalCaseId] = useState<string | number>();

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam === CaseSearchErrors.NOT_FOUND) {
      setError("No case found - check that all information is correct");
      if (setSelectedTab) setSelectedTab("lookup");
    }

    const externalMemberId = searchParams.get("external_member_id");
    const memberDOB = searchParams.get("member_date_of_birth");
    const caseId = searchParams.get("case_id");

    if (externalMemberId) {
      setOriginalMemberId(externalMemberId);
      const maskedMemberId = externalMemberId.replace(/[0-9a-zA-Z]/g, "*");
      setValue("external_member_id", maskedMemberId);
    }

    if (memberDOB) {
      setOriginalDOB(memberDOB);
      const maskedDOB = memberDOB.replace(/[0-9]/g, "*");
      setValue("member_date_of_birth", maskedDOB);
    }

    if (caseId) {
      setOriginalCaseId(caseId);
      const maskedCaseId = caseId.toString().replace(/[0-9]/g, "*");
      setValue("case_id", maskedCaseId);
    }

    const otherFields: (keyof HidenCaseSearchForm)[] = [
      "prescriber_npi",
      "prescriber_first",
      "prescriber_last",
    ];

    otherFields.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        setValue(field, value);
      }
    });
    setSearchParams({});
    getProvider();
  }, []);

  const getProvider = useCallback(async () => {
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const pmResponse = await getProviderManagementAPI(authToken);
      if (!pmResponse?.provider?.authorized_official_first_name) return;

      setValue("prescriber_npi", pmResponse.provider.billing_npi);
      setValue("prescriber_first", pmResponse.provider.authorized_official_first_name);
      setValue("prescriber_last", pmResponse.provider.authorized_official_last_name);
    } catch (error) {
      logEvent(ANALYTIC_EVENTS.ERRORS.providerManagementGet);
    }
  }, [getAuthToken, setValue]);

  const handleSearch = useCallback(async () => {
    logEvent(ANALYTIC_EVENTS.PA_CASE_SEARCH.search);

    const formValues = form.getValues();

    const memberId = originalMemberId || formValues.external_member_id;
    const dob = originalDOB || formValues.member_date_of_birth;
    const caseId = originalCaseId || formValues.case_id;

    setOriginalMemberId(memberId);
    setOriginalDOB(dob);
    setOriginalCaseId(caseId);

    if (numberValidator(caseId?.toString())) {
      setError("Case ID must be a number");
      return;
    }

    const dateError = isDate(dob);
    if (dob && dateError !== true) {
      setError(dateError as string);
      return;
    }

    const params = {
      case_id: caseId,
      prescriber_npi: formValues.prescriber_npi,
      external_member_id: memberId,
      member_date_of_birth: dob,
      prescriber_first: formValues.prescriber_first,
      prescriber_last: formValues.prescriber_last,
      error: CaseSearchErrors.NOT_FOUND,
    };

    const encodedParams = new URLSearchParams();
    for (const key in params) {
      if (params[key]) {
        encodedParams.append(key, params[key]);
      }
    }

    navigate({
      pathname: getThemeURL("PA_CASE"),
      search: encodedParams.toString(),
    });

  }, [navigate, originalMemberId, originalDOB, originalCaseId, form]);

  const handleMemberIdFocus = useCallback(() => {
    setValue("external_member_id", originalMemberId);
  }, [originalMemberId, setValue]);

  const handleMemberIdBlur = useCallback(() => {
    const memberId = form.getValues("external_member_id");
    setOriginalMemberId(memberId);
    const replaceVal = memberId.replace(new RegExp("[0-9a-zA-Z]", "g"), "*");
    setValue("external_member_id", replaceVal);
  }, [setValue, form]);

  const handleDOBFocus = useCallback(() => {
    setValue("member_date_of_birth", originalDOB);
  }, [originalDOB, setValue]);

  const handleDOBBlur = useCallback(() => {
    const memberDOB = form.getValues("member_date_of_birth");
    setOriginalDOB(memberDOB);
    const replaceDOBVal = memberDOB.replace(new RegExp("[0-9]", "g"), "*");
    setValue("member_date_of_birth", replaceDOBVal);
  }, [setValue, form]);

  const handleCaseIdFocus = useCallback(() => {
    setValue("case_id", originalCaseId || "");
  }, [originalCaseId, setValue]);

  const handleCaseIdBlur = useCallback(() => {
    const caseId = form.getValues("case_id");
    setOriginalCaseId(caseId);
    const replaceVal = caseId.toString().replace(new RegExp("[0-9]", "g"), "*");
    setValue("case_id", replaceVal);
  }, [setValue, form]);

  const handleChange = useCallback(() => {
    const memberDOB = form.getValues("member_date_of_birth");
    setOriginalDOB(memberDOB);
    const replaceDOBVal = formatDateWithSlashes(memberDOB);
    setValue("member_date_of_birth", replaceDOBVal);
  }, [setValue, form]);

  return (
    <div className={styles.CaseSearchContainer}>
      <Stack spacing="small" testId="test__prior_authorization_search_container">
        {showTitle && (
          <>
            <Heading className={styles.CaseSearchCenterText} testId="test__prior_authorization_search_heading">
              Prior Authorizations
            </Heading>
            <Body className={styles.CaseSearchCenterText} testId="test__prior_authorization_search_description">
              Find an existing case to add documents/check the status, or start a new case
            </Body>
          </>
        )}
        <Form form={form} onSubmit={() => handleSearch()}>
          <Stack>
            <TextField required label="NPI" name="prescriber_npi" testId="test__prior_authorization_search_NPI" />
            <TextField required label="Prescriber First Name" name="prescriber_first" testId="test__prior_authorization_search_first" />
            <TextField required label="Prescriber Last Name" name="prescriber_last" testId="test__prior_authorization_search_last" />
            <div onFocus={handleMemberIdFocus} onBlur={handleMemberIdBlur}>
              <TextField required label="Member ID" name="external_member_id" className={styles.textFieldWrapper} testId="test__prior_authorization_search_member_ID" />
            </div>
            <div onFocus={handleCaseIdFocus} onBlur={handleCaseIdBlur}>
              <TextField required label="Case ID" name="case_id" className={styles.textFieldWrapper} testId="test__prior_authorization_search_case_ID" />
            </div>
            <div onFocus={handleDOBFocus} onBlur={handleDOBBlur} onChange={handleChange}>
              <TextField
                instructions="mm/dd/yyyy"
                required
                label="Patient Date of Birth"
                name="member_date_of_birth"
                placeholder="__/__/____"
                className={styles.textFieldWrapper}
                testId="test__prior_authorization_search_DOB"
              />
            </div>
            {error && <Message appearance={Messages.Error} visible>{error}</Message>}
            <Shelf>
              <SubmitButton appearance={getThemeButton("primary", primary_color, primary_hover_color)} testId="test__prior_authorization_search_button">
                Search
              </SubmitButton>
            </Shelf>
          </Stack>
        </Form>
      </Stack>
    </div>
  );
};
